/**
 * NOTIFICATION CENTER
 * ===================
 * Real-time notification center for medical professionals.
 * 
 * - Doctor: Only CRITICAL patient notifications
 * - Nurse (IDE): ALL notifications (doctor actions, patient actions, etc.)
 */

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import api from "@/api";
import { 
  Bell, X, AlertTriangle, CheckCircle, FileText, 
  User, Upload, Hospital, Activity, Clock, MessageSquare,
  ChevronRight, Trash2, BellOff, RefreshCw
} from "lucide-react";
import { Link } from "react-router-dom";

export default function NotificationCenter() {
  const { user } = useAuth();
  const { on } = useWebSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const isDoctor = user?.role === "MEDECIN" || user?.role === "DOCTOR";
  const isNurse = user?.role === "IDE" || user?.role === "NURSE";
  const isSecretary = user?.role === "SECRETAIRE_MEDICAL";

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // WebSocket listeners for real-time updates
  useEffect(() => {
    const unsubscribers = [];

    // Listen for new alerts
    unsubscribers.push(on("new_alert", (data) => {
      // Doctor only gets CRITICAL alerts
      if (isDoctor && data.level !== "CRITICAL") return;
      
      addNotification({
        id: Date.now(),
        type: "alert",
        level: data.level,
        title: "Nouvelle alerte",
        message: `${data.patientName}: ${data.reason || "Alerte détectée"}`,
        patientId: data.patientId,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }));

    // Nurse gets all activity notifications
    if (isNurse) {
      unsubscribers.push(on("patient_checkin", (data) => {
        addNotification({
          id: Date.now(),
          type: "checkin",
          title: "Nouveau check-in",
          message: `${data.patientName} a effectué son check-in`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));

      unsubscribers.push(on("document_uploaded", (data) => {
        addNotification({
          id: Date.now(),
          type: "document",
          title: "Document uploadé",
          message: `${data.uploadedBy} a ajouté un document pour ${data.patientName}`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));

      unsubscribers.push(on("status_changed", (data) => {
        addNotification({
          id: Date.now(),
          type: "status",
          title: "Changement de statut",
          message: `${data.patientName}: ${data.oldStatus} → ${data.newStatus}`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));

      unsubscribers.push(on("hospitalization", (data) => {
        addNotification({
          id: Date.now(),
          type: "hospitalization",
          title: "Hospitalisation",
          message: `${data.patientName} a été hospitalisé`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));

      unsubscribers.push(on("alert_resolved", (data) => {
        addNotification({
          id: Date.now(),
          type: "resolved",
          title: "Alerte résolue",
          message: `${data.resolvedBy} a résolu l'alerte de ${data.patientName}`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));

      unsubscribers.push(on("new_message", (data) => {
        addNotification({
          id: Date.now(),
          type: "message",
          title: "Nouveau message",
          message: `Message de ${data.senderName}`,
          patientId: data.patientId,
          timestamp: new Date().toISOString(),
          read: false,
        });
      }));
    }

    return () => {
      unsubscribers.forEach(unsub => unsub?.());
    };
  }, [on, isDoctor, isNurse]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=20");
      const notifs = res.data.notifications || res.data || [];
      
      setNotifications(notifs);
      setUnreadCount(res.data.unreadCount ?? notifs.filter(n => !n.read).length);
    } catch (err) {
      // If endpoint doesn't exist, just use local state
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notif) => {
    setNotifications(prev => [notif, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
  };

  const markAsRead = async (notifId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notifId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    try {
      await api.patch(`/notifications/${notifId}/read`);
    } catch {
      // Ignore if endpoint doesn't exist
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    try {
      await api.patch("/notifications/mark-all-read");
    } catch {
      // Ignore if endpoint doesn't exist
    }
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const getNotificationIcon = (type, level) => {
    switch (type) {
      case "alert":
        return level === "CRITICAL" 
          ? <AlertTriangle className="w-4 h-4 text-rose-500" />
          : <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case "checkin":
        return <Activity className="w-4 h-4 text-emerald-500" />;
      case "document":
        return <Upload className="w-4 h-4 text-blue-500" />;
      case "status":
        return <RefreshCw className="w-4 h-4 text-purple-500" />;
      case "hospitalization":
        return <Hospital className="w-4 h-4 text-rose-500" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "message":
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case "appointment":
      case "appointment_request":
      case "order_new":
      case "new_order":
      case "order_result":
      case "validation_required":
      case "prechemo_decision":
        return <FileText className="w-4 h-4 text-cyan-600" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const getPatientLink = (patientId) => {
    if (!patientId) return null;
    return isDoctor ? `/doctor/patients/${patientId}` : `/nurse/patients/${patientId}`;
  };

  const getNotificationLink = (notif) => {
    if (isSecretary) return "/secretary/medical";
    if (notif.type === "message") {
      if (isDoctor) return "/doctor/messages";
      if (isNurse) return "/nurse/messages";
    }
    if (notif.type === "appointment") {
      if (isDoctor) return "/doctor/appointments";
      if (isNurse) return "/nurse/appointments";
    }
    if (["validation_required", "prechemo_decision"].includes(notif.type) && isDoctor) return "/doctor/dashboard";
    return getPatientLink(notif.patientId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Notifications</h3>
              <p className="text-xs text-slate-500">
                {isDoctor ? "Événements médicaux actionnables" : "Toute l'activité"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-primary hover:text-teal-700 font-medium"
                    data-testid="mark-all-read"
                  >
                    Tout lire
                  </button>
                  <button
                    onClick={clearAll}
                    className="p-1.5 hover:bg-slate-200 rounded-lg"
                    title="Effacer tout"
                  >
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </button>
                </>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-slate-200 rounded-lg"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellOff className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Aucune notification</p>
                <p className="text-xs text-slate-300 mt-1">
                  {isDoctor 
                    ? "Vous serez notifié des événements médicaux actionnables" 
                    : "Vous serez notifié de toute activité"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notif) => {
                  const link = getNotificationLink(notif);
                  const NotifContent = (
                    <div
                      className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                        !notif.read ? "bg-blue-50/50" : ""
                      }`}
                      onClick={() => markAsRead(notif.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-xl ${
                          notif.type === "alert" && notif.level === "CRITICAL"
                            ? "bg-rose-100"
                            : notif.type === "alert"
                            ? "bg-amber-100"
                            : notif.type === "checkin" || notif.type === "resolved"
                            ? "bg-emerald-100"
                            : notif.type === "hospitalization"
                            ? "bg-rose-100"
                            : "bg-slate-100"
                        }`}>
                          {getNotificationIcon(notif.type, notif.level)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm font-medium ${!notif.read ? "text-slate-900" : "text-slate-700"}`}>
                              {notif.title}
                            </p>
                            {!notif.read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 truncate">{notif.message}</p>
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getTimeAgo(notif.timestamp)}
                          </p>
                        </div>
                        {link && (
                          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );

                  return link ? (
                    <Link key={notif.id} to={link} onClick={() => setIsOpen(false)} data-testid={`notification-link-${notif.id}`}>
                      {NotifContent}
                    </Link>
                  ) : (
                    <div key={notif.id}>{NotifContent}</div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t bg-slate-50">
              <button
                onClick={fetchNotifications}
                className="w-full text-sm text-primary hover:text-teal-700 font-medium flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
